import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    question: 'What services does CJ Solutions offer?',
    answer:
      'CJ Solutions provides a comprehensive range of professional services including iGaming & FX Licensing, AML & KYC Outsourcing, Compliance Outsourcing, Tax & Payroll Outsourcing, Accounting Outsourcing, Legal Drafting Outsourcing, and Company Formation Services — all tailored to businesses operating in Cyprus and internationally.',
  },
  {
    question: 'How long does company formation in Cyprus take?',
    answer:
      'Company formation in Cyprus typically takes between 5 to 10 business days, depending on the type of entity and the completeness of the documentation provided. Our team handles the entire process end-to-end, ensuring a smooth and efficient registration.',
  },
  {
    question: 'Do you assist with iGaming and FX licensing?',
    answer:
      'Yes. We specialise in obtaining iGaming and FX licences from Cypriot and international regulatory authorities. Our experts guide you through the full application process — from pre-licensing assessment and documentation to submission and ongoing compliance support.',
  },
  {
    question: 'What does your AML & KYC outsourcing service include?',
    answer:
      'Our AML & KYC outsourcing covers customer due diligence (CDD), enhanced due diligence (EDD), transaction monitoring, suspicious activity reporting, and ongoing compliance reviews. We act as an extension of your compliance team, ensuring you meet all regulatory obligations.',
  },
  {
    question: 'Can you handle accounting and tax for non-resident companies?',
    answer:
      'Absolutely. We work with both resident and non-resident companies registered in Cyprus. Our accounting and tax services include bookkeeping, VAT registration and filing, corporate tax returns, and payroll management — all in full compliance with Cyprus tax law.',
  },
  {
    question: 'How quickly will I receive a response after submitting the form?',
    answer:
      'We aim to respond to all enquiries within 24 business hours. For urgent matters, you are welcome to reach us directly via WhatsApp or phone using the contact details listed on this page.',
  },
  {
    question: 'Is my information kept confidential?',
    answer:
      'Yes. All information shared with CJ Solutions is treated with strict confidentiality. We are fully compliant with GDPR and applicable data protection regulations. Your details will never be shared with third parties without your explicit consent.',
  },
  {
    question: 'Do you offer ongoing compliance support after initial setup?',
    answer:
      'Yes. We offer retainer-based ongoing compliance support, including regulatory updates, periodic reviews, and dedicated advisory services. Many of our clients retain us as their long-term compliance and corporate services partner.',
  },
];

export default function ContactFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: accordionRef, isVisible: accordionVisible } = useScrollAnimation();
  const [visibleItems, setVisibleItems] = useState<boolean[]>(Array(faqs.length).fill(false));

  useEffect(() => {
    if (!accordionVisible) return;
    faqs.forEach((_, i) => {
      setTimeout(() => {
        setVisibleItems((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
      }, 80 * i);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accordionVisible]);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left sticky panel */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-4 lg:sticky lg:top-32 transition-all duration-700 ${leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <span className="inline-block px-3 py-1 bg-amber-50 border border-amber-200 text-amber-600 text-xs font-semibold uppercase tracking-widest rounded mb-4">
              FAQ
            </span>
            <h2 className="text-4xl sm:text-5xl font-semibold text-slate-900 leading-tight mb-5">
              Frequently<br />Asked<br />
              <span className="text-amber-500">Questions</span>
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-6">
              Can't find the answer you're looking for? Reach out to our team directly and we'll be happy to help.
            </p>
            <a
              href="https://wa.me/35795981879"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg"></i>
              </div>
              Chat on WhatsApp
            </a>
          </div>

          {/* Accordion */}
          <div
            ref={accordionRef as React.RefObject<HTMLDivElement>}
            className="lg:col-span-8 space-y-3"
          >
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={index}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    isOpen ? 'border-amber-300 shadow-sm' : 'border-slate-200'
                  } ${visibleItems[index] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
                  style={{ transitionDuration: '550ms', transitionProperty: 'opacity, transform, border-color, box-shadow' }}
                >
                  <button
                    onClick={() => toggle(index)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-slate-50 transition-colors cursor-pointer"
                    aria-expanded={isOpen}
                  >
                    <span className="text-slate-900 font-medium text-sm sm:text-base">{faq.question}</span>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`text-amber-500 text-xl transition-transform duration-200 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
                    <p className="px-6 pb-5 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
